import { useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useReducedMotion } from '../tacticsHud';

/**
 * One movement arrow on the pitch — TubeGeometry along a Catmull-Rom
 * curve through the supplied control points, with a ConeGeometry tip
 * pointing in the direction of the last segment. Per spec §16.
 *
 * Points are world-XZ coordinates; the arrow is drawn at a small fixed
 * height above the grass so it reads above the pitch markings (when
 * those come back) without z-fighting the field plane.
 */
export interface MovementArrowSpec {
  id: string;
  /** Sequence of {x, z} world coordinates the arrow passes through. ≥ 2 points. */
  points: Array<{ x: number; z: number }>;
  /** CSS hex colour for the arrow body + tip. */
  color: string;
  /**
   * Optional player binding ("home-3" / "away-7"). When set, the arrow is
   * a movement run for that token — playback animates the player along
   * the curve. When unset, the arrow is a passive annotation.
   */
  playerKey?: string;
}

interface Props {
  arrow: MovementArrowSpec;
  /** Override the height above the grass — useful for stacking arrows. */
  yOffset?: number;
}

const ARROW_Y = 0.35;          // sit above the figures' shadows but below their feet
const TUBE_RADIUS = 0.32;
const TUBE_RADIAL_SEGMENTS = 8;
const TIP_RADIUS = 0.85;
const TIP_LENGTH = 1.6;

/** Draw-on duration in seconds. 3D mirrors the 2D `DRAW_ON_MS=350`
 *  but in seconds for `useFrame`'s elapsed-time convention. */
const DRAW_ON_S = 0.35;

export function MovementArrow3D({ arrow, yOffset = 0 }: Props) {
  const y = ARROW_Y + yOffset;
  const tubeMatRef = useRef<THREE.MeshStandardMaterial>(null);
  const tipMatRef = useRef<THREE.MeshStandardMaterial>(null);
  const mountedAt = useRef<number | null>(null);
  const reducedMotion = useReducedMotion();
  const [drawOnDone, setDrawOnDone] = useState(false);

  // useFrame ramps the materials' opacity from 0 → 1 over DRAW_ON_S
  // on first mount, then bails out so we don't burn the GL thread
  // animating after the arrow's settled. Reduced-motion bypasses
  // entirely — the materials render at full opacity from frame 1.
  useFrame((_, delta) => {
    if (drawOnDone || reducedMotion) return;
    const tube = tubeMatRef.current;
    const tip = tipMatRef.current;
    if (!tube || !tip) return;
    if (mountedAt.current === null) mountedAt.current = 0;
    mountedAt.current += delta;
    const t = Math.min(1, mountedAt.current / DRAW_ON_S);
    tube.opacity = t;
    tip.opacity = t;
    tube.transparent = t < 1;
    tip.transparent = t < 1;
    if (t >= 1) setDrawOnDone(true);
  });

  const { tubeGeo, tipGeo, tipPosition, tipQuaternion } = useMemo(() => {
    if (arrow.points.length < 2) {
      return { tubeGeo: null, tipGeo: null, tipPosition: null, tipQuaternion: null };
    }

    // Lift the points to the chosen height. Drop the last segment from the
    // tube so the cone tip can sit cleanly at the end.
    const v3 = arrow.points.map((p) => new THREE.Vector3(p.x, y, p.z));
    const curve = new THREE.CatmullRomCurve3(v3, false, 'catmullrom', 0.4);

    // Sample the curve to derive a tip position + orientation. Tube is
    // shortened slightly so the cone seats flush against its end.
    const sampleCount = Math.max(20, arrow.points.length * 8);
    const tipSamplePos = curve.getPoint(0.985);
    const tipTangent = curve.getTangent(0.985).normalize();

    // Trim the tube end so it stops just before the cone.
    const tubeCurve = new THREE.CatmullRomCurve3(
      [...v3.slice(0, -1), tipSamplePos],
      false,
      'catmullrom',
      0.4,
    );
    const tube = new THREE.TubeGeometry(tubeCurve, sampleCount, TUBE_RADIUS, TUBE_RADIAL_SEGMENTS, false);
    const tip = new THREE.ConeGeometry(TIP_RADIUS, TIP_LENGTH, 12);

    // Cone's default axis is +Y; rotate so it points along the curve tangent.
    const upY = new THREE.Vector3(0, 1, 0);
    const q = new THREE.Quaternion().setFromUnitVectors(upY, tipTangent);

    // Position the cone slightly forward so its base meets the tube end.
    const tipPos = tipSamplePos.clone().add(tipTangent.clone().multiplyScalar(TIP_LENGTH / 2));

    return { tubeGeo: tube, tipGeo: tip, tipPosition: tipPos, tipQuaternion: q };
  }, [arrow.points, y]);

  if (!tubeGeo || !tipGeo || !tipPosition || !tipQuaternion) return null;

  // Initial opacity: 0 when we're going to animate-in, 1 under
  // reduced-motion or after the first useFrame pass. The useFrame
  // hook overwrites these every animating frame.
  const initOpacity = reducedMotion ? 1 : 0;
  return (
    <group>
      <mesh geometry={tubeGeo}>
        <meshStandardMaterial
          ref={tubeMatRef}
          color={arrow.color}
          roughness={0.5}
          metalness={0.2}
          emissive={arrow.color}
          emissiveIntensity={0.25}
          transparent={initOpacity < 1}
          opacity={initOpacity}
        />
      </mesh>
      <mesh geometry={tipGeo} position={tipPosition} quaternion={tipQuaternion}>
        <meshStandardMaterial
          ref={tipMatRef}
          color={arrow.color}
          roughness={0.5}
          metalness={0.2}
          emissive={arrow.color}
          emissiveIntensity={0.35}
          transparent={initOpacity < 1}
          opacity={initOpacity}
        />
      </mesh>
    </group>
  );
}
